/**
 * PageError — canonical error surface for sysadmin pages.
 *
 * Per SYSADMIN-PLATFORM-SPEC §11.10 ("Error state"):
 *   - Inline <Alert variant="destructive"> at the top of the content area.
 *   - Half-page Card error treatment reserved for true 404 routes only.
 *
 * Replaces the scattered `Card className="border-destructive/30"` +
 * `CardContent ... text-destructive` pattern that grew up in
 * `routes/sysadmin/runtime.health.tsx`, `audit.stream.tsx`,
 * `audit.integrity.tsx`, etc.
 *
 * @example
 * if (error) {
 *   return <PageError title="Failed to load services" message={error.message} onRetry={refetch} />;
 * }
 */

import { AlertTriangle, RefreshCw } from 'lucide-react';
import type { ReactNode } from 'react';
import { Alert, AlertDescription, AlertTitle } from './alert';
import { Button } from './button';
import { cn } from '@/lib/utils';

export interface PageErrorProps {
  /** Headline — e.g. "Failed to load services" */
  title: string;
  /** Optional secondary explanation. Typically `error.message`. */
  message?: ReactNode;
  /** Optional retry handler. Renders a Retry button on the right. */
  onRetry?: () => void;
  /** Label for the retry button (default: "Retry") */
  retryLabel?: string;
  /** Disable retry button while fetching */
  retrying?: boolean;
  className?: string;
}

export function PageError({
  title,
  message,
  onRetry,
  retryLabel = 'Retry',
  retrying = false,
  className,
}: PageErrorProps) {
  return (
    <Alert variant="destructive" className={cn('relative', className)}>
      <AlertTriangle className="h-4 w-4" />
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <AlertTitle>{title}</AlertTitle>
          {message ? (
            <AlertDescription className="mt-1">{message}</AlertDescription>
          ) : null}
        </div>
        {onRetry ? (
          <Button
            variant="outline"
            size="sm"
            onClick={onRetry}
            disabled={retrying}
            className="shrink-0"
          >
            <RefreshCw className={cn('h-3.5 w-3.5 mr-1.5', retrying && 'animate-spin')} />
            {retryLabel}
          </Button>
        ) : null}
      </div>
    </Alert>
  );
}
